/**
 *
 *  Calendar
 *  DateTimePicker
 *
 *  Created by Vladimir Shishlyannikov on 12/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React from "react";
import { findDOMNode } from "react-dom";
import PropTypes from "prop-types";

import Header from "../Header/Header";
import WeekView from "../Week/Week";
// import MonthView from "../Month/Month";
import SlideTransitionGroup from "../SlideTransitionGroup/SlideTransitionGroup";

import moment from "moment-timezone";

// Utils
import * as dates from "../../../utils/dates";
import { getMomentWithTz } from "../../../utils/date";
import { uniqueId } from "../../../utils/_";

const VIEW_UNIT = {
  week: "day",
  month: "day",
};

const VIEW_OPTIONS = ["week", "month", "year", "decade", "century"];

const VIEW = {
  week: WeekView,
  // month: MonthView,
};

const propTypes = {
  /** @ignore */
  activeId: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,

  onChange: PropTypes.func,
  value: PropTypes.instanceOf(Date),

  /**
   * Available days of the week. If not present then it will be disabled.
   *
   * @example ['prop', ['availableDays', '['2018-11-03', ..]']]
   */
  availableDays: PropTypes.array,

  /**
   * The minimum date that the Calendar can navigate from.
   *
   * @example ['prop', ['min', 'new Date()']]
   */
  min: PropTypes.instanceOf(Date).isRequired,

  /**
   * The maximum date that the Calendar can navigate to.
   *
   * @example ['prop', ['max', 'new Date()']]
   */
  max: PropTypes.instanceOf(Date).isRequired,

  /**
   * Default current date at which the calendar opens. If none is provided, opens at today's date or the `value` date (if any).
   */
  currentDate: PropTypes.instanceOf(Date),

  /**
   * Change event Handler that is called when the currentDate is changed. The handler is called with the currentDate object.
   */
  onCurrentDateChange: PropTypes.func,

  /**
   * Controls the currently displayed calendar view. Use `defaultView` to set a unique starting view.
   *
   * @type {("month"|"year"|"decade"|"century")}
   * @controllable onViewChange
   */
  view(props, ...args) {
    return PropTypes.oneOf(props.views || VIEW_OPTIONS)(props, ...args);
  },

  /**
   * Defines a list of views the Calendar can traverse through, starting with the
   * first in the list to the last.
   *
   * @type array<"month"|"year"|"decade"|"century">
   */
  // views: PropTypes.arrayOf(PropTypes.oneOf(VIEW_OPTIONS)).isRequired,

  /**
   * A callback fired when the `view` changes.
   *
   * @controllable view
   */
  // onViewChange: PropTypes.func,

  /**
   * Callback fired when the Calendar navigates between views, or forward and backwards in time.
   *
   * @type function(date: ?Date, direction: string, view: string)
   */
  onNavigate: PropTypes.func,
  culture: PropTypes.string,

  /**
   * A formatter for the header button of the month view.
   *
   * @example ['dateFormat', ['headerFormat', "{ date: 'medium' }"]]
   */
  headerFormat: PropTypes.string,

  /**
   * A formatter calendar days of the week, the default formats each day as a Narrow name: "Mo", "Tu", etc.
   *
   * @example ['prop', { dayFormat: "day => \n['🎉', 'M', 'T','W','Th', 'F', '🎉'][day.getDay()]" }]
   */
  dayFormat: PropTypes.string,

  // isRtl: PropTypes.bool,

  /** @ignore */
  tabIndex: PropTypes.any,
};

class Calendar extends React.Component {
  static propTypes = propTypes;

  static defaultProps = {
    value: null,
    min: new Date(1900, 0, 1),
    max: new Date(2099, 11, 31),
    views: VIEW_OPTIONS,
    tabIndex: "0",
  };

  static Transition = SlideTransitionGroup;

  constructor(props) {
    super(props);

    const { view, views } = this.props;

    this.viewId = uniqueId("_calendar");
    this.labelId = uniqueId("_calendar_label");
    this.activeId = this.props.activeId || uniqueId("_calendar_active_cell");

    this.handleChange = this.handleChange.bind(this);
    this.handleMoveBack = this.handleMoveBack.bind(this);
    this.handleMoveForward = this.handleMoveForward.bind(this);

    this.state = {
      selectedIndex: 0,
      view: view || views[0],
    };
  }

  move(date, min, max, unit, direction) {
    var isUpOrDown = direction === "UP" || direction === "DOWN";
    var rangeUnit = "week";
    var addUnit = isUpOrDown ? "week" : "week";
    var amount = 1;
    var newDate;

    if (direction === "UP" || direction === "LEFT") amount *= -1;

    newDate = dates.add(date, amount, addUnit);

    return dates.inRange(newDate, min, max, rangeUnit) ? newDate : date;
  }

  UNSAFE_componentWillReceiveProps({
    messages,
    view,
    views,
    value,
    currentDate,
  }) {
    let val = this.inRangeValue(value);

    view = view || views[0];

    this.setState({
      view,
      slideDirection: this.getSlideDirection({ view, views, currentDate }),
    });

    //if the value changes reset views to the new one
    if (!dates.eq(val, dateOrNull(this.props.value), VIEW_UNIT[view])) {
      this.setCurrentDate(val, currentDate);
    }
  }

  handleMoveBack() {
    this.navigate("LEFT");
  }

  handleMoveForward() {
    this.navigate("RIGHT");
  }

  handleChange(date) {
    var { onChange } = this.props;

    this.setCurrentDate(date);

    onChange && onChange(date);

    this.focus();
  }

  render() {
    var {
      value,
      disabled,
      readOnly,
      availableDays,
      region,
      min,
      max,
      dayFormat,
      tabIndex,
    } = this.props;

    var { view, slideDirection } = this.state;
    var currentDate = this.getCurrentDate();

    var View = VIEW[view],
      todayDate = getMomentWithTz(new Date(), region).toDate();

    var key =
      "view_" +
      (view === "week"
        ? dates.diff(
            dates.startOf(min, "week"),
            dates.startOf(currentDate, "week"),
            "week"
          )
        : dates[view](currentDate));

    var isDisabled = disabled || readOnly;

    return (
      <div
        role="group"
        // focused={focused}
        tabIndex={tabIndex || 0}
        // onBlur={this.handleBlur}
        // onFocus={this.handleFocus}
        className={`tbk-border-b tbk-border-solid tbk-border-[#e3e7eb] tbk-py-3 tbk-px-2`}
        aria-activedescendant={this.activeId}
      >
        <Header
          label={this.getHeaderLabel()}
          prevLabel={this.getHeaderLabel("LEFT")}
          nextLabel={this.getHeaderLabel("RIGHT")}
          labelId={this.labelId}
          prevDisabled={
            isDisabled || !dates.inRange(this.nextDate("LEFT"), min, max, view)
          }
          nextDisabled={
            isDisabled || !dates.inRange(this.nextDate("RIGHT"), min, max, view)
          }
          onMoveLeft={this.handleMoveBack}
          onMoveRight={this.handleMoveForward}
        />
        <Calendar.Transition direction={slideDirection}>
          <View
            min={min}
            max={max}
            key={key}
            id={this.viewId}
            activeId={this.activeId}
            availableDays={availableDays}
            value={value}
            today={todayDate}
            dayFormat={dayFormat}
            region={region}
            disabled={disabled}
            prevDisabled={
              isDisabled ||
              !dates.inRange(this.nextDate("LEFT"), min, max, "week")
            }
            nextDisabled={
              isDisabled ||
              !dates.inRange(this.nextDate("RIGHT"), min, max, "week")
            }
            focused={currentDate}
            onChange={this.handleChange}
            aria-labelledby={this.labelId}
            onMoveLeft={this.handleMoveBack}
            onMoveRight={this.handleMoveForward}
          />
        </Calendar.Transition>
      </div>
    );
  }

  navigate(direction, date) {
    const { min, max, onNavigate, onViewChange } = this.props;
    var { view } = this.state;

    var slideDir = direction === "LEFT" ? "right" : "left";

    if (!date) {
      date =
        ["LEFT", "RIGHT"].indexOf(direction) !== -1
          ? this.nextDate(direction)
          : this.getCurrentDate();
    }

    if (dates.inRange(date, min, max, view)) {
      onNavigate && onNavigate(date, slideDir, view);

      this.focus(true);
      this.setCurrentDate(date);
      onViewChange && onViewChange(view);
    }
  }

  focus() {
    if (+this.props.tabIndex > -1) {
      findDOMNode(this).focus();
    }
  }

  getCurrentDate() {
    return this.props.currentDate || this.props.value || new Date();
  }

  setCurrentDate(date, currentDate = this.getCurrentDate()) {
    const { onCurrentDateChange } = this.props;

    var inRangeDate = this.inRangeValue(date ? new Date(date) : currentDate);

    if (
      dates.eq(inRangeDate, dateOrNull(currentDate), VIEW_UNIT[this.state.view])
    ) {
      return;
    }

    onCurrentDateChange && onCurrentDateChange(inRangeDate);
  }

  nextDate(direction) {
    var method = direction === "LEFT" ? "subtract" : "add",
      view = this.state.view,
      unit = view === "week" ? view : "month";

    return dates[method](this.getCurrentDate(), 1, unit);
  }

  getHeaderLabel(direction) {
    // eslint-disable-next-line
    var { culture, region, headerFormat } = this.props,
      currentDate = this.getCurrentDate();

    headerFormat = headerFormat || "MMMM";

    if (["LEFT", "RIGHT"].indexOf(direction) !== -1) {
      return getMomentWithTz(this.getCurrentDate(), region).format(
        headerFormat
      );
    }

    return getMomentWithTz(currentDate, region).format(headerFormat);
  }

  inRangeValue(_value) {
    var value = dateOrNull(_value);

    if (value === null) {
      return value;
    }

    return dates.max(dates.min(value, this.props.max), this.props.min);
  }

  getSlideDirection({ view, currentDate, views }) {
    var { currentDate: lastDate } = this.props;
    var { slideDirection, view: lastView } = this.state;

    if (lastView !== view) {
      return views.indexOf(lastView) > views.indexOf(view) ? "top" : "bottom";
    }

    if (lastDate !== currentDate) {
      return dates.gt(currentDate, lastDate) ? "left" : "right";
    }

    return slideDirection;
  }
}

function dateOrNull(dt) {
  if (dt && !isNaN(dt.getTime())) {
    return dt;
  }

  return null;
}

export default Calendar;
